import { useCallback, useState } from "react";
import styles from "@/interface/RightSheet/Presentations/PresentationsNavigator/PresentationView/PresentationView.module.scss";
import { autoScrollOnDrag } from "@/interface/RightSheet/Presentations/PresentationsNavigator/utils";
import type { DropPosition, PresentationData } from "../../types";

type UseFramesDnDProps = {
    frames: PresentationData["frames"];
    selected: string[];
    setSelected: (uids: string[]) => void;
    onReorder: (newFrames: PresentationData["frames"]) => void;
    listRef: React.RefObject<HTMLDivElement>;
};

export const useFramesDnD = ({
    frames,
    selected,
    setSelected,
    onReorder,
    listRef,
}: UseFramesDnDProps) => {
    const [draggedUIDs, setDraggedUIDs] = useState<string[]>([]);
    const [dragState, setDragState] = useState<{ uid: string; position: DropPosition } | null>(
        null,
    );

    const handleDragStart = useCallback(
        (uid: string, e: React.DragEvent<HTMLDivElement>) => {
            e.stopPropagation();

            // Если элемент не выделен, выделяем его
            if (!selected.includes(uid)) {
                setSelected([uid]);
            }

            setDraggedUIDs(selected.includes(uid) ? selected : [uid]);

            const slideEl = e.currentTarget;
            const contentEl = slideEl.querySelector(
                `.${styles.contentInner}`,
            ) as HTMLElement | null;

            if (contentEl) {
                const rect = contentEl.getBoundingClientRect();
                e.dataTransfer.setDragImage(contentEl, rect.width / 2, rect.height / 2);
            }

            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", uid);
        },
        [selected, setSelected],
    );

    const handleDragOver = useCallback(
        (uid: string, e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            if (draggedUIDs.includes(uid)) return;

            const rect = e.currentTarget.getBoundingClientRect();
            const middleY = rect.top + rect.height / 2;
            const position: DropPosition = e.clientY < middleY ? "above" : "below";

            setDragState((prev) => {
                if (prev?.uid === uid && prev.position === position) {
                    return prev;
                }
                return { uid, position };
            });
        },
        [draggedUIDs],
    );

    const handleDragLeave = useCallback(() => {
        setDragState(null);
    }, []);

    const handleDrop = useCallback(
        (uid: string, e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();

            if (!draggedUIDs.length) return;
            if (!dragState) return;

            // targetIndex для вставки
            const targetIndex = frames.findIndex((f) => f.UID === uid);
            if (targetIndex === -1) return;

            // сортируем dragged по текущему порядку в frames
            const draggedSet = new Set(draggedUIDs);
            const draggedFrames = frames.filter((f) => draggedSet.has(f.UID));
            const rest = frames.filter((f) => !draggedSet.has(f.UID));

            const insertIndex = dragState.position === "below" ? targetIndex + 1 : targetIndex;

            const newFrames = [...rest];
            newFrames.splice(insertIndex, 0, ...draggedFrames);

            onReorder(newFrames);

            setDraggedUIDs([]);
            setDragState(null);
        },
        [draggedUIDs, dragState, frames, onReorder],
    );

    const handleDragOverList = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            if (listRef.current) {
                autoScrollOnDrag(e.clientY, listRef.current);
            }
        },
        [listRef],
    );

    return {
        draggedUIDs,
        dragState,
        handleDragStart,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handleDragOverList,
    };
};
